import React, { useState, useRef } from "react";
import { useAtom } from "jotai";
import jwtDecode from "jwt-decode";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import env from "@beam-australia/react-env";

import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Password } from "primereact/password";
import useApi from "../../../hooks/useApi";
import "./login.css";

import { loadingState } from "../../../states/loadingState";

const Login = () => {
  const { t } = useTranslation();
  const api = useApi();
  const toast = useRef(null);

  const [isLoading, setIsLoading] = useAtom(loadingState);

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      nick: "",
      password: "",
    },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await api.post(
        `${env("API_URL")}/base/auth/backoffice`,
        data
      );

      localStorage.setItem("accessToken", response.data.accessToken.token);
      localStorage.setItem("refreshToken", response.data.refreshToken);

      const decodedToken = response.data.accessToken.token
        ? jwtDecode(response.data.accessToken.token)
        : null;

      localStorage.setItem("clientId", decodedToken.clientId);
      localStorage.setItem("clientName", decodedToken.clientName);
      localStorage.setItem("userRole", decodedToken.userRole);
      localStorage.setItem(
        "personName",
        decodedToken.personName ||
        decodedToken.userName ||
        decodedToken.name ||
        decodedToken.nick ||
        decodedToken.preferred_username ||
        decodedToken.unique_name ||
        decodedToken.sub ||
        ""
      );

      if (decodedToken.userRole == "SuperUsers") {
        navigate("/crud/clients");
      } else {
        navigate("/crud/applications");
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.current.show({
          severity: "error",
          summary: "Unauthorized",
          detail: t("errors.unauthorized"),
          life: 3000,
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: t("errors.login"),
          life: 3000,
        });
      }

      setIsLoading(false);
    }
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };

  return (
    <React.Fragment>
      <Toast ref={toast} />
      <div className="login-page">
        <div className="login-card">
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <img alt="Textum" src="/images/textum.svg" height="40" />
          </div>
          <h1>Textum Backoffice</h1>
          <p className="login-subtitle">{t("Login")}</p>
          <div className="container">
            <div className="flex justify-content-center">
              <form onSubmit={handleSubmit(onSubmit)} className="p-fluid" style={{ width: "100%" }}>
                <div className="field">
                  <Controller
                    name="nick"
                    control={control}
                    rules={{
                      required: t("forms.validation.required", {
                        entity: t("entities.administrators.nick"),
                      }),
                      maxLength: {
                        value: 30,
                        message: t("forms.validation.maxLength", {
                          entity: t("entities.administrators.nick"),
                          maxLength: 30,
                        }),
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <React.Fragment>
                        <div className="flex justify-content-between">
                          <label htmlFor="nick">
                            {t("entities.administrators.nick")}
                          </label>
                          <div>{getFormErrorMessage(errors.nick)}</div>
                        </div>
                        <div className="form-crud">
                          <div className="p-inputgroup">
                            <span className="p-inputgroup-addon">
                              <i className="pi pi-user"></i>
                            </span>
                            <span className="p-float-label">
                              <InputText
                                id="nick"
                                {...field}
                                value={field.value || ""}
                                maxLength={30}
                                className={[
                                  "form-control",
                                  classNames({
                                    "p-invalid": fieldState.invalid,
                                  }),
                                ]}
                              />
                            </span>
                          </div>
                        </div>
                      </React.Fragment>
                    )}
                  />
                </div>
                <div className="field">
                  <Controller
                    name="password"
                    control={control}
                    rules={{
                      required: t("forms.validation.required", {
                        entity: t("entities.administrators.password"),
                      }),
                      maxLength: {
                        value: 30,
                        message: t("forms.validation.maxLength", {
                          entity: t("entities.administrators.password"),
                          maxLength: 30,
                        }),
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <React.Fragment>
                        <div className="flex justify-content-between">
                          <label htmlFor="password">
                            {t("entities.administrators.password")}
                          </label>
                          <div>{getFormErrorMessage(errors.password)}</div>
                        </div>
                        <div className="form-crud">
                          <div className="p-inputgroup">
                            <span className="p-inputgroup-addon">
                              <i className="pi pi-key"></i>
                            </span>
                            <span className="p-float-label">
                              <Password
                                id="password"
                                {...field}
                                inputRef={field.ref}
                                className={classNames({
                                  "p-invalid": fieldState.error,
                                })}
                                feedback={false}
                              />
                            </span>
                          </div>
                        </div>
                      </React.Fragment>
                    )}
                  />
                </div>
                <Button
                  label={t("Login")}
                  icon="pi-sign-in pi "
                  className="p-button-raised col-12 md:col-4 p-button-primary"
                  loading={isLoading}
                />
              </form>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Login;
